import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { UpsellPageProductSource } from "@redotech/redo-conversion-core-common/upsell-page/upsell-page-types";
import { DiscountValueType } from "@redotech/redo-discounts-common/discount";
import { StorefrontClientContext } from "@redotech/redo-merchant-app-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { LabeledInput, LabelSize } from "@redotech/redo-web/labeled-input";
import EmptyProductImage from "@redotech/redo-web/layout-svgs/empty-product-image.svg";
import RedoAiRecommendationImage from "@redotech/redo-web/layout-svgs/redo-ai-recommendation-image.svg";
import ShopifyRecommendationImage from "@redotech/redo-web/layout-svgs/shopify-recommendation-image.svg";
import { Text } from "@redotech/redo-web/text";
import { getProduct } from "@redotech/shopify-storefront";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";
import {
  ShopifyProduct,
  transformToShopifyProduct,
  UpsellProductSelectionInProgress,
} from "../post-purchase-types";
import * as postPurchaseProductTileCss from "./post-purchase-product-tile.module.css";

export const PostPurchaseProductTile = memo(function PostPurchaseProductTile({
  product,
  stacked = false,
}: {
  product: UpsellProductSelectionInProgress;
  /**
   * Stack the tile vertically (image above details) for the narrow mobile
   * preview. Defaults to the desktop two-column layout.
   */
  stacked?: boolean;
}) {
  const client = useRequiredContext(StorefrontClientContext);

  const productLoad = useLoad<ShopifyProduct | undefined>(async () => {
    if (
      product.upsellProductSource !== UpsellPageProductSource.MANUAL ||
      !product.productId
    ) {
      return undefined;
    }

    const productInfo = await getProduct(client, product.productId);
    if (!productInfo) {
      return undefined;
    }

    const transformedProduct = transformToShopifyProduct(productInfo);
    transformedProduct.variants = transformedProduct.variants.filter(
      (variant) => product.variantIds?.includes(variant.admin_graphql_api_id),
    );

    return transformedProduct;
  }, [product, client]);

  const productTitle = () => {
    if (!product.upsellProductSource) {
      return "Select an upsell source";
    }

    switch (product.upsellProductSource) {
      case UpsellPageProductSource.MANUAL:
        return productLoad.value?.title ?? "Select an upsell source";
      case UpsellPageProductSource.COLLECTION:
        return "Random product from collection";
      case UpsellPageProductSource.SHOPIFY_RECOMMENDATION:
        return "Shopify recommendation";
      case UpsellPageProductSource.REDO_AI:
        return "Redo AI recommendation";
      default:
        assertNever(product);
    }
  };

  const productImage = () => {
    if (
      product.upsellProductSource ===
      UpsellPageProductSource.SHOPIFY_RECOMMENDATION
    ) {
      return (
        <ShopifyRecommendationImage
          className={postPurchaseProductTileCss.productImage}
        />
      );
    }

    if (product.upsellProductSource === UpsellPageProductSource.REDO_AI) {
      return (
        <RedoAiRecommendationImage
          className={postPurchaseProductTileCss.productImage}
        />
      );
    }

    const featuredVariantId =
      product.upsellProductSource === UpsellPageProductSource.MANUAL
        ? product.featuredVariantId
        : undefined;
    const featuredVariant = featuredVariantId
      ? productLoad.value?.variants.find(
          (v) => v.admin_graphql_api_id === featuredVariantId,
        )
      : undefined;
    const image = featuredVariant
      ? featuredVariant.image.src
      : productLoad.value?.variants.length === 1
        ? productLoad.value.variants[0]?.image.src
        : productLoad.value?.image.src;
    const altText = featuredVariant
      ? featuredVariant.image.altText
      : productLoad.value?.variants.length === 1
        ? productLoad.value.variants[0]?.image.altText
        : productLoad.value?.image.altText;
    return image ? (
      <img
        alt={altText || ""}
        className={postPurchaseProductTileCss.productImage}
        src={image}
      />
    ) : (
      <EmptyProductImage className={postPurchaseProductTileCss.productImage} />
    );
  };

  const productPrice = () => {
    return `$${parseFloat(productLoad.value?.variants[0]?.price ?? "0").toFixed(2)}`;
  };

  const compareAtPrice = () => {
    const compareAt = productLoad.value?.variants[0]?.compareAtPrice;
    if (!compareAt || parseFloat(compareAt) <= 0) {
      return null;
    }
    return `$${parseFloat(compareAt).toFixed(2)}`;
  };

  const referencePrice = () => {
    if (product.showCompareAtPrice) {
      const compare = compareAtPrice();
      if (compare) {
        return compare;
      }
    }
    return productPrice();
  };

  const discountedProductPrice = () => {
    if (
      !product.discountValueType ||
      product.upsellProductSource !== UpsellPageProductSource.MANUAL
    ) {
      return productPrice();
    }

    switch (product.discountValueType) {
      case DiscountValueType.PERCENTAGE: {
        const discountAmount = parseFloat(product.discountAmount ?? "1") / 100;
        const price = parseFloat(productLoad.value?.variants[0]?.price ?? "0");
        const finalPrice = price - price * discountAmount;

        return `$${finalPrice.toFixed(2)}`;
      }
      case DiscountValueType.AMOUNT: {
        const discountAmount = parseFloat(product.discountAmount ?? "0");
        const price = parseFloat(productLoad.value?.variants[0]?.price ?? "0");
        const finalPrice = price - discountAmount;
        return `$${finalPrice.toFixed(2)}`;
      }
      default:
        assertNever(product.discountValueType);
    }
  };

  const shouldShowStrikethrough = () => {
    const hasDiscount =
      product.discountAmount && product.discountAmount !== "0";
    const hasCompareAtPrice = product.showCompareAtPrice && compareAtPrice();
    return hasDiscount || hasCompareAtPrice;
  };

  return (
    <Flex
      align="center"
      bgColor="primary"
      dir="column"
      flex={1}
      minW="none"
      py="lg"
    >
      <Flex
        dir={stacked ? "column" : "row"}
        gap="lg"
        px={stacked ? "xl" : "3xl"}
        py={stacked ? "lg" : "3xl"}
        w="full"
      >
        <Flex
          align="center"
          flex={stacked ? undefined : 1}
          justify="center"
          maxHeight={stacked ? "quarter-screen" : undefined}
          minW="none"
          w={stacked ? "full" : undefined}
        >
          {productImage()}
        </Flex>
        <Flex
          dir="column"
          flex={stacked ? undefined : 1}
          minW="none"
          w={stacked ? "full" : undefined}
        >
          <Text fontSize="md" fontWeight="semibold" textColor="secondary">
            {productTitle()}
          </Text>
          <Flex dir="row" gap="md">
            <Text fontSize="xs" fontWeight="semibold">
              <span
                style={{
                  textDecoration: shouldShowStrikethrough()
                    ? "line-through"
                    : "none",
                }}
              >
                {referencePrice()}
              </span>
            </Text>
            {shouldShowStrikethrough() && (
              <Text fontSize="xs" fontWeight="semibold">
                {discountedProductPrice()}
              </Text>
            )}
          </Flex>
          {product.showDescription && (
            <Text fontSize="xs" fontWeight="thin">
              {product.customDescription || productLoad.value?.description}
            </Text>
          )}
          <Flex dir="column" gap="md" pt="md">
            {productLoad.value?.variants.length &&
              productLoad.value?.variants.length > 1 && (
                <LabeledInput label="Variants" size={LabelSize.EXTRA_SMALL}>
                  <RedoSingleSelectDropdownInput
                    disabled
                    options={[
                      {
                        value: "Option",
                        type: "text",
                        id: "Option",
                        text: "Option",
                      },
                    ]}
                    optionSelected={() => {}}
                    selectedItem={{
                      value: "Option",
                      type: "text",
                      id: "Option",
                      text: "Option",
                    }}
                    size="xs"
                  />
                </LabeledInput>
              )}
            <Divider />
            <Flex dir="column" gap="xl">
              <Flex justify="space-between">
                <Text fontSize="xs">Subtotal</Text>
                <Text fontSize="xs">{discountedProductPrice()}</Text>
              </Flex>
              <Flex justify="space-between">
                <Text fontSize="xs">Shipping</Text>
                <Text fontSize="xs">free</Text>
              </Flex>
              <Flex justify="space-between">
                <Text fontSize="xs">Tax</Text>
                <Text fontSize="xs">free</Text>
              </Flex>
              <Divider />
              <Flex justify="space-between">
                <Text fontSize="xs">Total</Text>
                <Text fontSize="xs">{discountedProductPrice()}</Text>
              </Flex>
              <RedoButton
                disabled
                hierarchy="primary"
                text={`Pay now ${discountedProductPrice()}`}
              />
              <RedoButton
                disabled
                hierarchy="secondary"
                text="Decline this offer"
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
});
